import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { Box, Button, Notification, Select, Spinner, StackView } from '@telus-uds/components-web';

import getStatusOptions from './utils';
import request from '../../../../../../utils/request';
import { isValidEmail } from '../../../../../../utils/utils';
import ProductConsoleId from './components/productConsoleId';
import ShippmentTrackingNumber from './components/shippmentTrackingNumber';
import ContractRecipient from './components/contractRecipient';
import OrderPlacedRecipient from './components/orderPlacedRecipient';
import {
  orderStatus as STATUS,
  statusMap
} from '../../../../../../utils/constant';
import { useLocalizedStringsContext } from '../../../../../../context/LocalizedStringsContext';

const OrderActions = ({ orderData }) => {
  const {
    ORDERS: {
      defaultOption,
      orderQuestion,
      save,
      emailDetail: { emptyEmail, invalidEmail }
    },
    iotOrderDetailsSection: {
      labelStatusUpdatedTo,
      labelSuccessfully,
      messageSelectAnAction
    }
  } = useLocalizedStringsContext();

  const { id } = orderData;
  const [status, setStatus] = useState('');
  // Initialize text-input states to '' (not undefined): UDS TextInput throws
  // "useInputValue started receiving `value` from parent after starting managing
  // own state" if value flips from undefined (uncontrolled) to a string (controlled).
  const [productConsoleId, setProductConsoleId] = useState('');
  const [shippmentTrackingNumber, setShippmentTrackingNumber] = useState('');
  const [contractRecipient, setContractRecipient] = useState({
    to: '',
    cc: ''
  });
  const [orderPlacedRecipients, setOrderPlacedRecipients] = useState('');
  const [feedback, setFeedback] = useState({ message: '', variant: '' });

  const getVariantObject = (v) => {
    if (v === 'error') return { validation: 'error' };
    if (v === 'success') return { style: 'success' };
    return {};
  };
  const [loading, setLoading] = useState(false);

  const onUpdateStatus = async () => {
    try {
      if (!status) {
        setFeedback({
          message: messageSelectAnAction,
          variant: 'error'
        });
        return;
      }

      if (status === STATUS.contractSent) {
        if (!contractRecipient.to) {
          setFeedback({ message: emptyEmail, variant: 'error' });
          return;
        }
        if (!isValidEmail(contractRecipient.to)) {
          setFeedback({ message: invalidEmail, variant: 'error' });
          return;
        }
      }

      setLoading(true);
      const payload = { status };
      if (productConsoleId) payload.iotProductConsoleOrderId = productConsoleId;
      if (shippmentTrackingNumber)
        payload.iotShippmentTrackingNumber = shippmentTrackingNumber;
      if (contractRecipient.to) payload.contractEmail = contractRecipient.to;
      if (contractRecipient.cc) payload.contractEmailCC = contractRecipient.cc;
      if (status === STATUS.contractSent) payload.isContractResend = false;
      if (orderPlacedRecipients)
        payload.orderPlacedRecipients = orderPlacedRecipients;

      const result = await request.put(`/orders/${id}`, payload);

      if (result)
        setFeedback({
          message: `${labelStatusUpdatedTo} ${statusMap[status]} ${labelSuccessfully}`,
          variant: 'success'
        });
      setLoading(false);
      setStatus('');
    } catch (err) {
      setFeedback({
        message: 'Error occoured while updating the status',
        variant: 'error'
      });
      setLoading(false);
      setStatus('');
    }
  };

  useEffect(() => {
    const { email_signingAuthority: signingAuthorityEmail } = orderData;
    // Coerce to '' so TextInput's value never flips from undefined (uncontrolled) to a string (controlled).
    setContractRecipient(prev => ({
      ...prev,
      to: signingAuthorityEmail || ''
    }));
  }, [orderData]);

  return (
    <StackView space={4}>
      {feedback.message && (
        <Notification
          variant={getVariantObject(feedback.variant)}
          dismissible
          onExit={() => setFeedback({ message: '', variant: '' })}
          copy="en"
        >
          {feedback.message}
        </Notification>
      )}
      <Select
        label={orderQuestion}
        placeholder={defaultOption}
        value={status}
        onChange={(value) => {
          setFeedback({ message: '', variant: '' });
          setStatus(value);
        }}
      >
        {getStatusOptions(orderData) && getStatusOptions(orderData).map(option => (
          <Select.Item key={option.value} value={option.value}>
            {option.text}
          </Select.Item>
        ))}
      </Select>
      {status === STATUS.orderPlaced && (
        <ProductConsoleId
          productConsoleId={productConsoleId}
          setProductConsoleId={setProductConsoleId}
        />
      )}
      {status === STATUS.orderShipped && (
        <ShippmentTrackingNumber
          shippmentTrackingNumber={shippmentTrackingNumber}
          setShippmentTrackingNumber={setShippmentTrackingNumber}
        />
      )}
      {status === STATUS.contractSent && (
        <ContractRecipient
          contractRecipient={contractRecipient}
          setContractRecipient={setContractRecipient}
        />
      )}
      {status === STATUS.orderPlaced && (
        <OrderPlacedRecipient
          orderPlacedRecipients={orderPlacedRecipients}
          setOrderPlacedRecipients={setOrderPlacedRecipients}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Spinner label="Updating..." variant={{ size: 'small' }} show={loading} inline>
          <Button variant={{ priority: 'high' }} onPress={onUpdateStatus}>
            {save}
          </Button>
        </Spinner>
      </div>
    </StackView>
  );
};

OrderActions.propTypes = {
  orderData: PropTypes.shape({
    id: PropTypes.string,
    email_signingAuthority: PropTypes.string
  }).isRequired
};

export default OrderActions;
